import { Alert, Spin } from 'antd';
import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { RENDER_PREVIEW_DEBOUNCE_MS } from '../constants';
import {
  formatPresetErrorMessage,
  shortsRenderPresetService,
} from '../services/shortsRenderPresetService';
import { RenderPreviewRequest } from '../types/shortsRenderPresetType';

interface RenderPreviewPaneProps {
  /**
   * `null` 이면 아직 미리볼 수 없는 상태 — 호출하지 않고 `blockedReason` 을 보여준다.
   * 값이 바뀌면 디바운스 후 새로 그린다.
   */
  request: RenderPreviewRequest | null;
  /** `request` 가 null 인 이유. 예: "콘텐츠 ID를 입력하세요". */
  blockedReason?: string;
  /** 이미지 영역 높이(px). 세로 결과물이라 기본값을 넉넉히 잡는다. */
  height?: number;
  /** 이미지 위에 함께 렌더할 컨트롤 (콘텐츠 선택 등). */
  children?: React.ReactNode;
}

const formatPreviewError = (error: unknown): string => {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status === 404) {
    return '콘텐츠 또는 프리셋을 찾을 수 없습니다. 다른 platform 의 것이거나 삭제되었습니다.';
  }
  // 400 은 errors.preview 로 온다 — "영상도 대표 썸네일도 없음", 폰트 파일 없음, 렌더 타임아웃 등.
  return formatPresetErrorMessage(error, '미리보기를 만들지 못했습니다');
};

/**
 * 서버가 실제 ffmpeg 으로 그린 정지 프레임을 보여주는 패널.
 *
 * 값이 바뀔 때마다 부르면 API 서버에서 ffmpeg 이 그만큼 도는 호출이라, 값이 멈춘 뒤
 * 한 번만 부른다(디바운스 + 이전 요청 abort). 한 장에 1~3초 걸리므로 로딩 중에도
 * 직전 이미지를 지우지 않고 위에 덮어 깜빡임을 줄인다.
 */
const RenderPreviewPane: React.FC<RenderPreviewPaneProps> = ({
  request,
  blockedReason,
  height = 420,
  children,
}) => {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  // 이전 objectURL 을 revoke 하려면 렌더 사이에 살아남는 참조가 필요하다.
  const urlRef = useRef<string | null>(null);

  // 객체 아이덴티티가 아니라 값으로 비교해야 부모의 매 렌더마다 재요청하지 않는다.
  const payload = request ? JSON.stringify(request) : '';

  useEffect(() => {
    if (!payload) {
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();

    const timer = setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const blob = await shortsRenderPresetService.fetchPreview(
          JSON.parse(payload) as RenderPreviewRequest,
          controller.signal
        );
        if (cancelled) {
          return;
        }
        const next = URL.createObjectURL(blob);
        if (urlRef.current) {
          URL.revokeObjectURL(urlRef.current);
        }
        urlRef.current = next;
        setUrl(next);
      } catch (err) {
        // 값이 또 바뀌어 밀려난 요청 — 사용자에겐 실패가 아니다.
        if (cancelled || axios.isCancel(err)) {
          return;
        }
        setError(formatPreviewError(err));
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }, RENDER_PREVIEW_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [payload]);

  // 마지막 objectURL 은 언마운트 때 정리한다. 위 effect 에서 하면 매 요청마다 revoke 되어
  // 화면에 걸린 이미지가 깨진다.
  useEffect(
    () => () => {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
      }
    },
    []
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {children}

      {error && <Alert type="error" showIcon message={error} />}

      <div
        style={{
          position: 'relative',
          height,
          background: '#f5f5f5',
          border: '1px solid #f0f0f0',
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {url ? (
          <img
            src={url}
            alt="쇼츠 렌더 미리보기"
            style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
          />
        ) : (
          !loading && (
            <span style={{ color: '#888', fontSize: 13, padding: 16, textAlign: 'center' }}>
              {blockedReason ?? '미리보기가 아직 없습니다.'}
            </span>
          )
        )}

        {loading && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              // 직전 이미지를 지우지 않고 덮어야 값 조정 중 깜빡이지 않는다.
              background: url ? 'rgba(255, 255, 255, 0.6)' : 'transparent',
            }}
          >
            <Spin tip="렌더 중… (1~3초)" />
          </div>
        )}
      </div>
    </div>
  );
};

export default RenderPreviewPane;
